/**
 * @file pickingRecovery.js
 * @description Self-healing helpers for the picking PWA on the tablets.
 *
 * The picking surface is the one screen in this app where nobody can "just go
 * back to the dashboard": a picker is mid-batch, holding a trolley, on a
 * tablet with no keyboard and no devtools. Anything that leaves the screen
 * blank costs a walk back to the office and a refresh.
 *
 * Two ways it goes blank today:
 *   1. PickingModal is a lazy chunk (App.js) behind a Suspense fallback that
 *      is a plain full-screen rectangle. The service worker serves /static/*
 *      network-first, so on patchy warehouse Wi-Fi the chunk request can hang
 *      with nothing on screen; after a deploy the old chunk hash can 404
 *      outright and the import rejects.
 *   2. A rejected lazy import (or any render throw) escapes Suspense to the
 *      app-root ErrorBoundary, which unmounts the whole tree and shows a
 *      Polaris page with a manual "Refresh" button — and on a chunk 404 that
 *      button is the only thing that helps.
 *
 * This module is the pure, unit-testable half of the fix, mirroring
 * wallboard/wallboardRecovery.js: a crash-reload backoff so a genuinely
 * broken build cannot thrash the tablet, and chunk-error detection so we can
 * say "a new version was deploying" instead of dumping a stack trace at
 * somebody holding a trolley.
 */

const CRASH_ATTEMPT_KEY = 'pickingCrashReload';

/**
 * Reload delays per consecutive crash. Much tighter at the top than the
 * wallboard's ladder: a picker is standing there waiting, so the first
 * recovery has to feel like a slow page load, not an outage. It still grows,
 * because a build that crashes every time must not reload-loop forever.
 */
export const CRASH_RELOAD_DELAYS_MS = [2000, 5000, 15000, 60000];

/** A crash this long after the previous one is a fresh run, not a loop. */
export const CRASH_STREAK_RESET_MS = 5 * 60 * 1000;

function safeStorage(storage) {
  // Private mode / blocked site data: every accessor can throw, and a picking
  // tablet that cannot remember a streak should still recover, just without
  // memory across reloads.
  if (storage) return storage;
  try {
    return window.sessionStorage;
  } catch (_) {
    return null;
  }
}

function readAttempt(storage) {
  try {
    const raw = storage.getItem(CRASH_ATTEMPT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed.count !== 'number') return null;
    return parsed;
  } catch (_) {
    return null;
  }
}

/**
 * True for the family of errors webpack/the browser raise when a lazily
 * imported chunk cannot be fetched or parsed. Covers CRA's ChunkLoadError,
 * the raw "Loading chunk 42 failed" message, and the native dynamic-import
 * rejection text used by newer browsers.
 * @param {unknown} error
 * @returns {boolean}
 */
export function isChunkLoadError(error) {
  const name = String(error?.name || '');
  const message = String(error?.message || error || '');
  return /ChunkLoadError/i.test(name)
    || /Loading (?:CSS )?chunk\s+\S+\s+failed/i.test(message)
    || /dynamically imported module/i.test(message)
    || /Importing a module script failed/i.test(message);
}

/**
 * Record one more crash and return how long to wait before reloading.
 * @param {{ storage?: Storage|null, now?: number }} [options]
 * @returns {{ delayMs: number, attempt: number }}
 */
export function recordCrashAndGetDelay({ storage, now = Date.now() } = {}) {
  const store = safeStorage(storage);
  const prev = store ? readAttempt(store) : null;
  const streak = prev && now - (prev.at || 0) < CRASH_STREAK_RESET_MS ? prev.count + 1 : 1;
  if (store) {
    try {
      store.setItem(CRASH_ATTEMPT_KEY, JSON.stringify({ count: streak, at: now }));
    } catch (_) {
      // Quota / private mode — back off anyway, just without memory.
    }
  }
  const idx = Math.min(streak, CRASH_RELOAD_DELAYS_MS.length) - 1;
  return { delayMs: CRASH_RELOAD_DELAYS_MS[idx], attempt: streak };
}

/** Called once picking has mounted and rendered: the streak is over. */
export function clearCrashStreak({ storage } = {}) {
  const store = safeStorage(storage);
  if (!store) return;
  try {
    store.removeItem(CRASH_ATTEMPT_KEY);
  } catch (_) {
    // ignore
  }
}

/**
 * Should the boundary reload the page by itself?
 *
 * A chunk error is always worth one automatic reload: hosting serves
 * index.html no-store, so a reload lands the new build and the import
 * succeeds. A render crash is reloaded once too — the common cause is stale
 * state from the previous build — but only once, because past that a reload
 * is just a loop in front of a picker who would rather see the error and get
 * a supervisor. `attempt` is the streak from recordCrashAndGetDelay.
 * @param {{ error?: unknown, attempt?: number }} params
 * @returns {boolean}
 */
export function shouldAutoReload({ error, attempt = 1 } = {}) {
  if (attempt > CRASH_RELOAD_DELAYS_MS.length) return false;
  if (isChunkLoadError(error)) return true;
  return attempt <= 2;
}
